import { prixAdherentReference, prixPromoAdherentReference } from './boutiqueClubArticlePricing';

const nombre = (valeur) => valeur === '' || valeur == null ? null : Number(valeur);
const arrondi = (valeur) => Math.round((Number(valeur) + Number.EPSILON) * 100) / 100;

export function lignesCompositionPack(packId, produits = [], composants = []) {
  return composants
    .filter((item) => item.pack_id === packId)
    .sort((a, b) => Number(a.ordre || 0) - Number(b.ordre || 0))
    .map((item) => ({ ...item, article: produits.find((produit) => produit.id === item.produit_composant_id) || item.article || null }));
}

export function articleVendableIndividuellement(article) {
  if (!article || article.est_pack) return false;
  // L'appartenance à un Pack ne décide plus de la vente individuelle. Le
  // réglage explicite permet les trois cas métier : individuel, Pack, ou les
  // deux. L'absence du réglage conserve la vente individuelle historique.
  return article.options_boutique?.vente_individuelle !== false;
}

export function tarificationPack(pack = {}, produits = [], composants = [], campagne = {}) {
  const lignes = lignesCompositionPack(pack.id, produits, composants);
  let lignesValorisees = lignes;
  let groupes = [];
  if (Array.isArray(pack.groupes_choix_pack) && pack.groupes_choix_pack.length) {
    try {
      groupes = groupesChoixPack(pack, composants);
      const idsAlternatives = new Set(groupes.flatMap((groupe) => groupe.produit_ids));
      const ligneParProduit = new Map(lignes.map((ligne) => [ligne.produit_composant_id, ligne]));
      lignesValorisees = [
        ...lignes.filter((ligne) => !idsAlternatives.has(ligne.produit_composant_id)),
        ...groupes.flatMap((groupe) => groupe.produit_ids.map((produitId) => ({
          ...ligneParProduit.get(produitId),
          quantite: groupe.quantite / groupe.produit_ids.length,
          groupe_choix_id: groupe.id,
        }))),
      ];
    } catch (_) {
      return { reference: null, automatique: null, ajustement: nombre(pack.ajustement_pack_ttc) || 0, net: null, economie: null, remiseEquivalente: null };
    }
  }
  // Le snapshot serveur historique reste la référence des Packs classiques.
  // Les Packs à choix sont valorisés dans le pilotage selon la moyenne validée
  // des alternatives ; le prix d'une sélection adhérent reste calculé à part.
  if (pack.tarification_pack && !groupes.length) {
    const serveur = pack.tarification_pack;
    return {
      reference: nombre(serveur.prix_reference_ttc),
      automatique: nombre(serveur.prix_propose_automatique_ttc),
      ajustement: nombre(serveur.ajustement_pack_ttc) || 0,
      net: nombre(serveur.prix_net_ttc),
      economie: nombre(serveur.economie_ttc),
      remiseEquivalente: nombre(serveur.remise_equivalente_pourcent),
    };
  }
  if (!lignesValorisees.length || lignesValorisees.some(({ article }) => prixAdherentReference(article) == null)) {
    return { reference: null, automatique: null, ajustement: nombre(pack.ajustement_pack_ttc) || 0, net: null, economie: null, remiseEquivalente: null };
  }
  const reference = arrondi(lignesValorisees.reduce((total, { article, quantite }) => total + Number(quantite) * Number(prixAdherentReference(article)), 0));
  const automatique = arrondi(lignesValorisees.reduce((total, { article, quantite }) => {
    const adherent = Number(prixAdherentReference(article));
    const promo = prixPromoAdherentReference(article);
    return total + Number(quantite) * (promo != null && promo < adherent ? promo : adherent);
  }, 0));
  const mode = pack.remise_mode || 'AUCUNE';
  const taux = mode === 'GENERAL' ? Number(campagne.remise_generale_pourcent || 0)
    : mode === 'SPECIFIQUE' ? nombre(pack.remise_specifique_pourcent) : null;
  const ajustement = nombre(pack.ajustement_pack_ttc) || 0;
  const net = taux == null ? arrondi(automatique + ajustement) : arrondi(reference * (1 - taux / 100));
  const economie = arrondi(reference - net);
  const remiseEquivalente = reference ? arrondi((reference - net) * 100 / reference) : null;
  return { reference, automatique, ajustement, net, economie, remiseEquivalente };
}

export function groupesChoixPack(pack = {}, composants = []) {
  if (!Array.isArray(pack.groupes_choix_pack) || !pack.groupes_choix_pack.length) return [];
  const autorises = new Set(composants.filter((item) => item.pack_id === pack.id).map((item) => item.produit_composant_id));
  const vus = new Set();
  return pack.groupes_choix_pack.map((groupe, index) => {
    const ids = [...new Set((Array.isArray(groupe.produit_ids) ? groupe.produit_ids : []).filter((id) => autorises.has(id) && !vus.has(id)))];
    ids.forEach((id) => vus.add(id));
    const quantite = Number(groupe.quantite);
    if (!String(groupe.nom || '').trim() || !Number.isSafeInteger(quantite) || quantite < 1 || quantite > 999 || !ids.length) throw new Error(`Le groupe de choix ${index + 1} est incomplet.`);
    return { id:String(groupe.id || `groupe-${index + 1}`), nom:String(groupe.nom).trim(), quantite, produit_ids:ids };
  });
}

export function compositionPackSelectionnee(pack = {}, composants = [], selection = {}) {
  const lignes = composants.filter((item) => item.pack_id === pack.id);
  const groupes = groupesChoixPack(pack, composants);
  if (!groupes.length) return lignes;
  const idsAutorises = new Set(groupes.flatMap((groupe) => groupe.produit_ids));
  const inconnus = Object.entries(selection).filter(([id, quantite]) => Number(quantite || 0) > 0 && !idsAutorises.has(id));
  if (inconnus.length) throw new Error('La composition contient un article non autorisé.');
  groupes.forEach((groupe) => {
    const total = groupe.produit_ids.reduce((somme, id) => somme + Number(selection[id] || 0), 0);
    if (!Number.isSafeInteger(total) || total !== groupe.quantite) throw new Error(`Sélectionnez exactement ${groupe.quantite} article(s) dans le groupe « ${groupe.nom} ».`);
  });
  return lignes.filter((ligne) => Number(selection[ligne.produit_composant_id] || 0) > 0)
    .map((ligne) => ({ ...ligne, quantite:Number(selection[ligne.produit_composant_id]) }));
}

export function tarificationPackSelection(pack = {}, produits = [], composants = [], campagne = {}, selection = {}) {
  const composition = compositionPackSelectionnee(pack, composants, selection);
  const sansSnapshot = { ...pack, tarification_pack:null, groupes_choix_pack:null };
  return tarificationPack(sansSnapshot, produits, composition, campagne);
}

// Pour un pack à choix, le catalogue doit annoncer un prix réellement
// achetable. On retient donc, groupe par groupe, la référence disponible la
// moins chère (et non la somme de toutes les alternatives exposées).
export function tarificationMinimumPackChoix(pack = {}, produits = [], composants = [], campagne = {}) {
  const groupes = groupesChoixPack(pack, composants);
  if (!groupes.length) return null;
  const ligneParProduit = new Map(composants
    .filter((item) => item.pack_id === pack.id)
    .map((item) => [item.produit_composant_id, item]));
  const selectionMinimum = {};
  const selectionMaximum = {};
  for (const groupe of groupes) {
    const candidats = groupe.produit_ids.map((id) => {
      const article = produits.find((produit) => produit.id === id);
      const ligne = ligneParProduit.get(id);
      const reference = prixAdherentReference(article);
      const promo = prixPromoAdherentReference(article);
      const prix = promo != null && reference != null && promo < reference ? promo : reference;
      return { id, ligne, prix: nombre(prix) };
    }).filter((candidat) => candidat.ligne && candidat.prix != null && Number.isFinite(candidat.prix) && candidat.prix >= 0);
    if (!candidats.length) return null;
    candidats.sort((a, b) => a.prix - b.prix || String(a.id).localeCompare(String(b.id)));
    selectionMinimum[candidats[0].id] = groupe.quantite;
    selectionMaximum[candidats[candidats.length - 1].id] = groupe.quantite;
  }
  const minimum = tarificationPackSelection(pack, produits, composants, campagne, selectionMinimum);
  const maximum = tarificationPackSelection(pack, produits, composants, campagne, selectionMaximum);
  if (minimum.net == null) return null;
  return {
    ...minimum,
    variable: maximum.net != null && Number(maximum.net) !== Number(minimum.net),
    maximum: maximum.net,
    selection: selectionMinimum,
  };
}

export function margePack(pack = {}, produits = [], composants = [], campagne = {}) {
  const lignesBrutes = lignesCompositionPack(pack.id, produits, composants);
  let lignes = lignesBrutes;
  if (Array.isArray(pack.groupes_choix_pack) && pack.groupes_choix_pack.length) {
    try {
      const groupes = groupesChoixPack(pack, composants);
      const idsAlternatives = new Set(groupes.flatMap((groupe) => groupe.produit_ids));
      const ligneParProduit = new Map(lignesBrutes.map((ligne) => [ligne.produit_composant_id, ligne]));
      lignes = [
        ...lignesBrutes.filter((ligne) => !idsAlternatives.has(ligne.produit_composant_id)),
        ...groupes.flatMap((groupe) => groupe.produit_ids.map((produitId) => ({
          ...ligneParProduit.get(produitId),
          quantite: groupe.quantite / groupe.produit_ids.length,
        }))),
      ];
    } catch (_) { return null; }
  }
  const tarif = tarificationPack(pack, produits, composants, campagne);
  if (!lignes.length || tarif.reference == null || tarif.net == null || tarif.reference <= 0) return null;
  const incomplet = lignes.some(({ article }) => {
    const snapshot = article?.source_snapshot;
    const interclub = String(article?.source_origine || '').toUpperCase() === 'INTERCLUB';
    const paReel = interclub ? article?.prix_achat_source_disponible === true
      : !!snapshot && Object.prototype.hasOwnProperty.call(snapshot, 'prix_achat_unitaire_ht') && snapshot.prix_achat_unitaire_ht != null;
    return !article || prixAdherentReference(article) == null || nombre(article.tva_pourcent) == null
      || nombre(article.prix_achat_ht) == null || !paReel;
  });
  if (incomplet) return null;
  const paTotal = arrondi(lignes.reduce((total, { article, quantite }) => total + Number(quantite) * Number(article.prix_achat_ht), 0));
  const pvHt = arrondi(lignes.reduce((total, { article, quantite }) => {
    const poids = Number(prixAdherentReference(article)) * Number(quantite) / tarif.reference;
    return total + (tarif.net * poids) / (1 + Number(article.tva_pourcent) / 100);
  }, 0));
  if (pvHt <= 0) return null;
  return { paTotal, pvHt, pourcent: arrondi((pvHt - paTotal) * 100 / pvHt) };
}

export function validerCompositionPack(composition = []) {
  const ids = new Set();
  return composition.map((item, ordre) => {
    const quantite = Number(item.quantite);
    if (!item.produit_id || ids.has(item.produit_id)) throw new Error('Un article ne peut apparaître qu’une fois dans le Pack.');
    if (!Number.isSafeInteger(quantite) || quantite < 1 || quantite > 999) throw new Error('Les quantités doivent être des entiers entre 1 et 999.');
    ids.add(item.produit_id);
    return { produit_id: item.produit_id, quantite, ordre };
  });
}
